import client from 'client'
import type {
  ResendAssetFileAssetAssetIdFilesFileIdPostMutationRequest,
  ResendAssetFileAssetAssetIdFilesFileIdPostMutationResponse,
  ResendAssetFileAssetAssetIdFilesFileIdPostPathParams,
  ResendAssetFileAssetAssetIdFilesFileIdPostQueryParams,
  ResendAssetFileAssetAssetIdFilesFileIdPost422,
} from '../../types/ResendAssetFileAssetAssetIdFilesFileIdPost.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const resendAssetFileAssetAssetIdFilesFileIdPostMutationKey = () => [{ url: '/asset/{asset_id}/files/{file_id}' }] as const

export type ResendAssetFileAssetAssetIdFilesFileIdPostMutationKey = ReturnType<typeof resendAssetFileAssetAssetIdFilesFileIdPostMutationKey>

/**
 * @summary Resend Asset File
 * {@link /asset/:asset_id/files/:file_id}
 */
export async function resendAssetFileAssetAssetIdFilesFileIdPost(
  asset_id: ResendAssetFileAssetAssetIdFilesFileIdPostPathParams['asset_id'],
  file_id: ResendAssetFileAssetAssetIdFilesFileIdPostPathParams['file_id'],
  data: ResendAssetFileAssetAssetIdFilesFileIdPostMutationRequest,
  params?: ResendAssetFileAssetAssetIdFilesFileIdPostQueryParams,
  config: Partial<RequestConfig<ResendAssetFileAssetAssetIdFilesFileIdPostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    ResendAssetFileAssetAssetIdFilesFileIdPostMutationResponse,
    ResponseErrorConfig<ResendAssetFileAssetAssetIdFilesFileIdPost422>,
    ResendAssetFileAssetAssetIdFilesFileIdPostMutationRequest
  >({ method: 'POST', url: `/asset/${asset_id}/files/${file_id}`, params, data, ...requestConfig })
  return res
}

/**
 * @summary Resend Asset File
 * {@link /asset/:asset_id/files/:file_id}
 */
export function useResendAssetFileAssetAssetIdFilesFileIdPost(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<ResendAssetFileAssetAssetIdFilesFileIdPostMutationResponse>,
      ResponseErrorConfig<ResendAssetFileAssetAssetIdFilesFileIdPost422>,
      {
        asset_id: ResendAssetFileAssetAssetIdFilesFileIdPostPathParams['asset_id']
        file_id: ResendAssetFileAssetAssetIdFilesFileIdPostPathParams['file_id']
        data: ResendAssetFileAssetAssetIdFilesFileIdPostMutationRequest
        params?: ResendAssetFileAssetAssetIdFilesFileIdPostQueryParams
      }
    >
    client?: Partial<RequestConfig<ResendAssetFileAssetAssetIdFilesFileIdPostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? resendAssetFileAssetAssetIdFilesFileIdPostMutationKey()

  return useMutation<
    ResponseConfig<ResendAssetFileAssetAssetIdFilesFileIdPostMutationResponse>,
    ResponseErrorConfig<ResendAssetFileAssetAssetIdFilesFileIdPost422>,
    {
      asset_id: ResendAssetFileAssetAssetIdFilesFileIdPostPathParams['asset_id']
      file_id: ResendAssetFileAssetAssetIdFilesFileIdPostPathParams['file_id']
      data: ResendAssetFileAssetAssetIdFilesFileIdPostMutationRequest
      params?: ResendAssetFileAssetAssetIdFilesFileIdPostQueryParams
    }
  >({
    mutationFn: async ({ asset_id, file_id, data, params }) => {
      return resendAssetFileAssetAssetIdFilesFileIdPost(asset_id, file_id, data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}